import {
  ITableCell,
  ITableSortLabel,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  ITypography,
  IDirection
} from '@sanlam-glacier/app-common/components';
import clsx from 'clsx';
import { ToDoListTableHeaderConfig } from 'modules/dashboard/types';
import styles from './to-do-list.module.scss';

export type ToDoListTableHeaderProps<T> = {
  headers: ToDoListTableHeaderConfig<T>[];
  orderBy?: string;
  handleSort?: (
    property: ToDoListTableHeaderConfig<T>['key'] | string,
    direction: IDirection
  ) => void;
  direction?: ITableSortLabel['direction'];
  align?: ITableCell['align'];
  tableHeaderClassName?: string;
  disabled?: boolean;
  actionHeaderKey?: string;
  hoverable?: boolean;
  sticky?: boolean;
  textAlign?: ITypography['align'];
};

export const ToDoListTableHeader = <T,>({
  headers,
  handleSort,
  align,
  tableHeaderClassName,
  disabled,
  actionHeaderKey,
  hoverable,
  sticky
}: ToDoListTableHeaderProps<T>) => {
  const testComponentName = 'ToDoList';

  const hasSortHandler = !!handleSort;

  const getCellAlignment = (header: ToDoListTableHeaderConfig<T>) => {
    if (header.align === 'left') return TableCell.ALIGN.LEFT;
    if (header.align === 'right') return TableCell.ALIGN.RIGHT;
  };

  const getTypographyAlignment = (header: ToDoListTableHeaderConfig<T>) => {
    if (header.align === 'left') return Typography.ALIGN.LEFT;
    if (header.align === 'right') return Typography.ALIGN.RIGHT;
  };

  const getSortLabelAlignmentClass = (header: ToDoListTableHeaderConfig<T>) => {
    if (header.align === 'right') return 'table-cell-sort-right';
    return '';
  };

  return (
    <TableHead sticky={sticky}>
      <TableRow className={tableHeaderClassName} disabled={disabled} hoverable={hoverable}>
        {headers.map((header, index) => (
          <TableCell
            key={(header.key as string) ?? index}
            align={getCellAlignment(header) ?? align}
            variant={TableCell.VARIANT.HEAD}
            className={`inner-header-cell ${header.className} ${
              header.key && header.key === actionHeaderKey ? 'table-action-column' : ''
            }`}
          >
            {header.renderCellContent ? (
              header.renderCellContent()
            ) : hasSortHandler && header.key ? (
              <TableSortLabel
                data-testid={`${testComponentName}SortOn${String(header.key)}`}
                className={clsx(
                  `${tableHeaderClassName} ${getSortLabelAlignmentClass(header)}`,
                  styles.tableHeaders
                )}
                active={hasSortHandler}
                direction={header.direction as unknown as ITableSortLabel['direction']}
                onClick={() => handleSort(header.key, header.direction)}
              >
                <HeaderLabel title={header.label}>{header.label}</HeaderLabel>
              </TableSortLabel>
            ) : (
              <HeaderLabel align={getTypographyAlignment(header)} title={header.label}>
                {header.label}
              </HeaderLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
type HeaderProps = {
  children: string;
  align?: ITypography['align'];
  title?: string;
};
const HeaderLabel = ({ children, align, title }: HeaderProps) => (
  <Typography
    weight={Typography.WEIGHT.MEDIUM}
    variant={Typography.VARIANT.B3}
    color={Typography.COLOR.SECONDARY}
    className={`table-header-label`}
    align={align}
    title={title}
  >
    {children}
  </Typography>
);
